import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CertificateCreateRequest, CertificateItemDto} from '../../../../models/generated-models';
import {AddCertificateModalComponent, CertificateModalResult} from '../../../pages/user-certificate/user-certificate-modal';
import {UserCertificateApiService} from '../../../pages/user-certificate/user-certificate-api.service';
import {ToastService} from '../toast/toast-service';

/**
 * "เพิ่มใบรับรอง" beside the ThaiTraces sync button on the business form: the business keys in a
 * certificate that มกอช. does not hold (GMP, HACCP, …) and it is saved without DGA / ThaiD.
 */
@Component({
  selector: 'app-certificate-add-button',
  standalone: true,
  template: `
    <button type="button"
            class="btn btn-sm btn-outline-primary-accent d-inline-flex align-items-center gap-1 mb-2 text-nowrap"
            title="เพิ่มใบรับรองที่ไม่ได้ออกโดย มกอช. เช่น GMP, HACCP"
            [disabled]="isSaving"
            (click)="open()">
      @if (isSaving) {
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      } @else {
        <i class="bi bi-plus-lg"></i>
      }
      เพิ่มใบรับรอง
    </button>
  `
})
export class CertificateAddButtonComponent {
  /** Shown in the modal header context only; the certificate belongs to the signed-in user. */
  @Input() ownerName: string | null | undefined;
  @Input() taxId: string | null | undefined;
  @Output() added = new EventEmitter<CertificateItemDto>();

  private readonly modalService = inject(NgbModal);
  private readonly certificateApi = inject(UserCertificateApiService);
  private readonly toast = inject(ToastService);

  isSaving = false;

  open(): void {
    if (this.isSaving) {
      return;
    }
    const modalRef = this.modalService.open(AddCertificateModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'user-certificate-modal'
    });
    const modal = modalRef.componentInstance as AddCertificateModalComponent;
    modal.ownerName = `${this.ownerName ?? ''}`;
    modal.taxId = `${this.taxId ?? ''}`;
    modal.mode = 'create';
    modal.manualEntry = true;

    modalRef.result.then((result: CertificateModalResult | undefined) => {
      if (result) {
        this.save(result);
      }
    }).catch(() => undefined);
  }

  /** Creates the certificate, then uploads its scan when one was picked (work item 170). */
  private save(result: CertificateModalResult): void {
    const {attachment, removeAttachment, ...request} = result;
    this.isSaving = true;
    this.certificateApi.createCertificate(request as CertificateCreateRequest).subscribe({
      next: (saved) => {
        if (attachment && saved.id != null) {
          this.certificateApi.uploadAttachment(saved.id, attachment).subscribe({
            next: (withFile) => this.finish(withFile),
            // The certificate exists already; the interceptor reported the upload failure.
            error: () => this.finish(saved)
          });
          return;
        }
        this.finish(saved);
      },
      error: () => {
        this.isSaving = false;
      }
    });
  }

  private finish(saved: CertificateItemDto): void {
    this.isSaving = false;
    this.toast.success('บันทึกใบรับรองเรียบร้อยแล้ว');
    this.added.emit(saved);
  }
}
